import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Row, Col, Alert, Spinner
} from 'react-bootstrap';
import { FaEdit, FaTrashAlt, FaPlus } from 'react-icons/fa';
import { pricingAPI } from '../../api/apiClient';
import Swal from 'sweetalert2';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/css/CarPrice.css';
import PaginationComponent from '../common/PaginationComponent';
import { useTranslation } from 'react-i18next';

function CarPrice() {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [highlightedId, setHighlightedId] = useState(null);
  const [form, setForm] = useState({
    from_dis: '',
    to_dis: '',
    price_vehicle1_city: '',
    price_vehicle2_city: '',
    price_vehicle3_city: '',
    price_vehicle4_city: '',
    price_vehicle1_suburb: '',
    price_vehicle2_suburb: '',
    price_vehicle3_suburb: '',
    price_vehicle4_suburb: ''
  });
  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0
  });

  const CarsName = [
    { 
      name: 'Caddy'
    },
    { 
      name: 'Bus'
    },
    { 
      name: 'Maxi Bus'
    },
    { 
      name: 'Planenbus'
    },
  ]

  const fetchData = async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      const response = await pricingAPI.distances.index({
        page,
        per_page: pagination.per_page
      });

      if (response.success) {
        setData(response.data.data || response.data);
        setPagination(response.data.meta || {
          current_page: page,
          last_page: 1,
          per_page: 10,
          total: response.data.length
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: response.message || t('failed_to_fetch_data'),
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: t('failed_to_fetch_data'),
        timer: 2000,
        showConfirmButton: false
      });
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openForm = (entry = null) => {
    if (entry) {
      setForm({
        from_dis: entry.from_dis || '',
        to_dis: entry.to_dis || '',
        price_vehicle1_city: entry.price_vehicle1_city || '',
        price_vehicle2_city: entry.price_vehicle2_city || '',
        price_vehicle3_city: entry.price_vehicle3_city || '',
        price_vehicle4_city: entry.price_vehicle4_city || '',
        price_vehicle1_suburb: entry.price_vehicle1_suburb || '',
        price_vehicle2_suburb: entry.price_vehicle2_suburb || '',
        price_vehicle3_suburb: entry.price_vehicle3_suburb || '',
        price_vehicle4_suburb: entry.price_vehicle4_suburb || ''
      });
      setEditData(entry);
      setHighlightedId(entry.id);
    } else {
      setForm({
        from_dis: '',
        to_dis: '',
        price_vehicle1_city: '',
        price_vehicle2_city: '',
        price_vehicle3_city: '',
        price_vehicle4_city: '',
        price_vehicle1_suburb: '',
        price_vehicle2_suburb: '',
        price_vehicle3_suburb: '',
        price_vehicle4_suburb: ''
      });
      setEditData(null);
      setHighlightedId(null);
    }
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');
      const fd = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        fd.append(key, value);
      });

      let response;
      if (editData) {
        response = await pricingAPI.distances.update(editData.id, fd);
      } else {
        response = await pricingAPI.distances.store(fd);
      }

      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: t('success'),
          text: t('data_saved_successfully'),
          timer: 2000,
          showConfirmButton: false
        });
        setShowForm(false);
        fetchData(pagination.current_page); // Refresh current page
        if (editData) {
          setHighlightedId(editData.id);
        }
      } else {
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: response.message || t('failed_to_save_data'),
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: t('failed_to_save_data'),
        timer: 2000,
        showConfirmButton: false
      });
      console.error('Error saving data:', err);
    } finally {
      setLoading(false);
    }
  };

  const openDelete = (id) => {
    setDeleteId(id);
    setShowDelete(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await pricingAPI.distances.delete(deleteId);
      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: t('success'),
          text: t('data_deleted_successfully'),
          timer: 2000,
          showConfirmButton: false
        });
        setShowDelete(false);
        fetchData(pagination.current_page); // Refresh current page
      } else {
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: response.message || t('failed_to_delete_data'),
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: t('failed_to_delete_data'),
        timer: 2000,
        showConfirmButton: false
      });
      console.error('Error deleting data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  return (
      <div className="py-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="dashboard-title">🚚{t('Preis_entfernung')}</h3>
          <Button className='add-btn' onClick={() => openForm()}>
            <FaPlus className="me-1" /> {t('Neu_hinzufügen')}
          </Button>
        </div>
        {error && <Alert variant="danger">{error}</Alert>}
        {loading ? (
          <div className="text-center">
            <Spinner animation="border" />
          </div>
        ) : data.length === 0 ? (
          <Alert variant="info">{t('no_data_available')}</Alert>
        ) : (
          <div className="table-container shadow-sm rounded">
            <div className='table-responsive'>
              <Table bordered hover className="custom-table mb-0">
                <thead className="table-dark text-center">
                  <tr>
                    <th>#</th>
                    <th>{t("from")}</th>
                    <th>{t("to")}</th>
                    {/* {[1, 2, 3, 4].map(i => (
                      <th key={`s${i}`}>{t("vehicle")} {i} {t("city")}</th>
                    ))}
                    {[1, 2, 3, 4].map(i => (
                      <th key={`v${i}`}>{t("vehicle")} {i} {t("suburb")}</th>
                    ))} */}
                    {CarsName.map(({name}, i) => (
                      <th key={`s${i}`}>{name} {t("city")}</th>
                    ))}
                    {CarsName.map(({name}, i) => (
                      <th key={`s${i}`}>{name} {t("suburb")}</th>
                    ))}
                    <th>{t("action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((entry, idx) => (
                    <tr key={entry.id} className={`text-center ${highlightedId === entry.id ? 'row-highlighted' : ''}`}>
                      <td>{(pagination.current_page - 1) * pagination.per_page + idx + 1}</td>
                      <td>{entry.from_dis}</td>
                      <td>{entry.to_dis}</td>
                      <td>{entry.price_vehicle1_city}</td>
                      <td>{entry.price_vehicle2_city}</td>
                      <td>{entry.price_vehicle3_city}</td>
                      <td>{entry.price_vehicle4_city}</td>
                      <td>{entry.price_vehicle1_suburb}</td>
                      <td>{entry.price_vehicle2_suburb}</td>
                      <td>{entry.price_vehicle3_suburb}</td>
                      <td>{entry.price_vehicle4_suburb}</td>
                      <td>
                        <Button
                          className="add-btn me-2"
                          size="sm"
                          onClick={() => openForm(entry)}
                          title="Bearbeiten"
                          disabled={loading}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => {
                            setDeleteId(entry.id);
                            setShowDelete(true);
                          }}
                          title={t("Delete")}
                          disabled={loading}
                        >
                          <FaTrashAlt />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        )}
        {!loading && pagination.total > 0 && (
          <PaginationComponent
            currentPage={pagination.current_page}
            lastPage={pagination.last_page}
            onPageChange={fetchData}
            disabled={loading}
          />
        )}


      {/* Add/Edit Modal */}
      <Modal 
        show={showForm} 
        onHide={() => {
          setShowForm(false);
          if (editData) {
            setHighlightedId(editData.id);
          }
        }} 
        size="xl" 
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{editData ? 'Bearbeiten' : 'Hinzufügen'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="mb-3 gy-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t("from")}</Form.Label>
                  <Form.Control
                    type="number"
                    value={form.from_dis}
                    onChange={e => setForm(prev => ({ ...prev, from_dis: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t("to")}</Form.Label>
                  <Form.Control
                    type="number"
                    value={form.to_dis}
                    onChange={e => setForm(prev => ({ ...prev, to_dis: e.target.value }))}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* {("city")} Prices */}
            <h6 className="mt-4 mb-3">{t("city_prices")}</h6>
            <Row className="mb-3 gy-2">
              {/* {CarsName.map((car, i) => (
                <Col md={6} key={`city-${i - 1}`}>
                  <Form.Group>
                    <Form.Label>{car.name} {t("city")}</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={form[`price_vehicle${i}_city`]}
                      onChange={e => setForm(prev => ({ ...prev, [`price_vehicle${i}_city`]: e.target.value }))}
                    />
                  </Form.Group>
                </Col>
              ))} */}
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Caddy {t("city")}</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={form[`price_vehicle1_city`]}
                    onChange={e => setForm(prev => ({ ...prev, [`price_vehicle1_city`]: e.target.value }))}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Bus {t("city")}</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={form[`price_vehicle2_city`]}
                    onChange={e => setForm(prev => ({ ...prev, [`price_vehicle2_city`]: e.target.value }))}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Maxi Bus {t("city")}</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={form[`price_vehicle3_city`]}
                    onChange={e => setForm(prev => ({ ...prev, [`price_vehicle3_city`]: e.target.value }))}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Planenbus {t("city")}</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={form[`price_vehicle4_city`]}
                    onChange={e => setForm(prev => ({ ...prev, [`price_vehicle4_city`]: e.target.value }))}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* {("suburb")} Prices */}
            <h6 className="mt-4 mb-3">{t("suburb_prices")}</h6>
            <Row className="mb-3 gy-2">
              {/* {CarsName.map((car, i) => (
                <Col md={6} key={`suburb-${i}`}>
                  <Form.Group>
                    <Form.Label>{car.name} {t("suburb")}</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={form[`price_vehicle${i}_suburb`]}
                      onChange={e => setForm(prev => ({ ...prev, [`price_vehicle${i}_suburb`]: e.target.value }))}
                    />
                  </Form.Group>
                </Col>
              ))} */}
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Caddy {t("suburb")}</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={form[`price_vehicle1_suburb`]}
                    onChange={e => setForm(prev => ({ ...prev, [`price_vehicle1_suburb`]: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Bus {t("suburb")}</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={form[`price_vehicle2_suburb`]}
                    onChange={e => setForm(prev => ({ ...prev, [`price_vehicle2_suburb`]: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Maxi Bus {t("suburb")}</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={form[`price_vehicle3_suburb`]}
                    onChange={e => setForm(prev => ({ ...prev, [`price_vehicle3_suburb`]: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Planenbus {t("suburb")}</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={form[`price_vehicle4_suburb`]}
                    onChange={e => setForm(prev => ({ ...prev, [`price_vehicle4_suburb`]: e.target.value }))}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowForm(false)} disabled={loading}>
            {t("cancel")}
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={loading || !form.from_dis || !form.to_dis}>
            {loading ? t('Saving...') : t("save")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal show={showDelete} onHide={() => setShowDelete(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("confirm_deletion")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t("confirm_delete_entry")}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDelete(false)} disabled={loading}>
            {t("cancel")}
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={loading}>
            {loading ? t('deleting') : t("delete")}
          </Button>
        </Modal.Footer>
      </Modal>
</div>
  );
}

export default CarPrice;